/**
 * 插件系统根 Provider：创建 `PluginManager`、注册各 RPC 服务、加载内置插件，
 * 并在主题变化时向所有插件 iframe 广播 CSS 变量；同时挂载 `PluginPanelHost` 以响应 Worker 的 `ui.openPanel`。
 */
import type { PluginManifest } from "@aspect/plugin-sdk";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { useTheme, useColorMode } from "@/shared/store/theme-store";

import { PluginManagerContext } from "./plugin-manager-context";
import { PluginPanelHost } from "./plugin-panel-host";
import { PluginManager, collectCssVariables } from "../runtime/plugin-manager";
import { contextMenuServiceHandler } from "../services/context-menu-service";
import { dataServiceHandler } from "../services/data-service";
import { storageServiceHandler } from "../services/storage-service";
import { themeServiceHandler } from "../services/theme-service";
import { toolbarServiceHandler } from "../services/toolbar-service";
import { uiServiceHandler } from "../services/ui-service";
import { userServiceHandler } from "../services/user-service";

interface PluginProviderProps {
  children: ReactNode;
  /** 启动时按顺序加载并尝试激活的内置插件列表（manifest + 资源 baseUrl） */
  builtinPlugins?: Array<{ manifest: PluginManifest; baseUrl: string }>;
}

export function PluginProvider({ children, builtinPlugins = [] }: PluginProviderProps) {
  const managerRef = useRef<PluginManager | null>(null);
  const [manager, setManager] = useState<PluginManager | null>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const mgr = new PluginManager();

    mgr.router.register("ui", uiServiceHandler);
    mgr.router.register("theme", themeServiceHandler);
    mgr.router.register("storage", storageServiceHandler);
    mgr.router.register("user", userServiceHandler);
    mgr.router.register("data", dataServiceHandler);
    mgr.router.register("contextMenu", contextMenuServiceHandler);
    mgr.router.register("toolbar", toolbarServiceHandler);

    managerRef.current = mgr;
    setManager(mgr);

    (async () => {
      for (const { manifest, baseUrl } of builtinPlugins) {
        try {
          await mgr.loadPlugin(manifest, baseUrl);
          if (mgr.isPluginEnabled(manifest.id)) {
            await mgr.activatePlugin(manifest.id);
          }
        } catch (err) {
          console.error(`[PluginHost] 加载插件 "${manifest.id}" 失败:`, err);
        }
      }
    })();

    return () => {
      // 组件卸载时清理所有插件
    };
  }, [builtinPlugins]);

  const theme = useTheme();
  const colorMode = useColorMode();

  useEffect(() => {
    const mgr = managerRef.current;
    if (!mgr) return;

    // DOM 更新后（useThemeEffect 先于此 effect 运行），
    // 读取计算后的 CSS 变量并广播给所有插件
    requestAnimationFrame(() => {
      const isDark = document.documentElement.classList.contains("dark");
      mgr.bridge.broadcastEvent("theme-changed", {
        theme: isDark ? "dark" : "light",
        cssVariables: collectCssVariables(),
      });
    });
  }, [theme, colorMode]);

  return (
    <PluginManagerContext.Provider value={manager}>
      {children}
      <PluginPanelHost />
    </PluginManagerContext.Provider>
  );
}
